import { ReportHeader } from '../../header/report-header';
import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DataTablePageEvent } from 'primereact/datatable';
import { formatDate } from '../../../../utils/date-format';
import { InstallmentBankTransferSideBarFilter } from './side-bar/installment-bank-transfer-side-bar-filter';
import { Tag } from 'primereact/tag';
import { getReportInstallmentBankTransferOperation } from './operations/get-report-installment-bank-transfer-operation';
import { InstallmentBankTransferReportRow } from '../../../../api/types/reports/accountant/installment-bank-transfer-report.type';
import { PaymentInstallmentStatus } from '../../../../api/types/reports/accountant/installment.type';
import { InstallmentBankTransferSideBarFilterType } from './side-bar/installment-bank-transfer-side-bar-filter.type';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';
import { toast } from 'react-toastify';
import { createInstallmentBankTransferExcelData } from './operations/create-installment-bank-transfer-excel-data';
import { Button } from 'primereact/button';
import { WarningDialog } from '../../../../component/ui/warning-dialog/warning-dialog';
import { payAllInstallmentsBankTransferOperation } from './operations/pay-all-installments-bank-transfer-operation';
import { generateMasavFileOperation } from './operations/generate-masav-file-operation';
import { RefundAndFailBankTransferInstallmentDialog } from './components/refund-and-fail-bank-transfer-installment-dialog';

export const ReportInstallmentBankTransfer = () => {
  const [data, setData] = useState<InstallmentBankTransferReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [payAllLoading, setPayAllLoading] = useState(false);
  const [showPayAllWarning, setShowPayAllWarning] = useState(false);
  const [masavLoading, setMasavLoading] = useState(false);
  const [showMasavWarning, setShowMasavWarning] = useState(false);
  const [refundFailRow, setRefundFailRow] =
    useState<InstallmentBankTransferReportRow | null>(null);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] =
    useState<InstallmentBankTransferSideBarFilterType>({
      branchIds: [],
      includeUnassignedBranches: true,
      selectedMonth: new Date(),
      paymentStatuses: [],
    });

  const fetchReportData = async (
    filters: InstallmentBankTransferSideBarFilterType,
    newOffset: number = offset,
    newLimit: number = limit,
  ) => {
    setFilters(filters);
    setOffset(newOffset);
    setLimit(newLimit);
    await getReportInstallmentBankTransferOperation(
      filters,
      newOffset,
      newLimit,
      setData,
      setTotal,
      setLoading,
    );
  };

  const getStatusSeverity = (
    status: PaymentInstallmentStatus,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case PaymentInstallmentStatus.PAID:
        return 'success';
      case PaymentInstallmentStatus.PENDING:
        return 'warning';
      case PaymentInstallmentStatus.FAILED:
      case PaymentInstallmentStatus.CANCELLED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: PaymentInstallmentStatus): string => {
    switch (status) {
      case PaymentInstallmentStatus.PENDING:
        return 'ממתין לתשלום';
      case PaymentInstallmentStatus.PAID:
        return 'שולם';
      case PaymentInstallmentStatus.FAILED:
        return 'נכשל';
      case PaymentInstallmentStatus.CANCELLED:
        return 'בוטל';
      default:
        return 'לא ידוע';
    }
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  const getSelectedMonthLabel = (): string => {
    if (!filters.selectedMonth) return '';
    return filters.selectedMonth.toLocaleDateString('he-IL', {
      month: 'long',
      year: 'numeric',
    });
  };

  const handlePayAllClick = () => {
    setShowPayAllWarning(true);
  };

  const payAllWindow = useMemo(() => {
    if (!filters.selectedMonth) {
      return null;
    }

    const y = filters.selectedMonth.getFullYear();
    const m = filters.selectedMonth.getMonth();

    // Charge window is ±7 days around the 20th of the same month.
    // Use local dates at midday to avoid DST edge cases.
    const center = new Date(y, m, 20, 12, 0, 0, 0);
    const start = new Date(center);
    start.setDate(center.getDate() - 7);
    const end = new Date(center);
    end.setDate(center.getDate() + 7);

    return { start, end };
  }, [filters.selectedMonth]);

  const canPayAll = useMemo(() => {
    if (!payAllWindow) return false;
    const now = new Date();
    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      12,
    );
    return today >= payAllWindow.start && today <= payAllWindow.end;
  }, [payAllWindow]);

  const payAllBlockedTooltip = useMemo(() => {
    if (!payAllWindow) return 'יש לבחור חודש כדי לגבות תשלומים';
    const startLabel = payAllWindow.start.toLocaleDateString('he-IL');
    const endLabel = payAllWindow.end.toLocaleDateString('he-IL');
    return `ניתן לבצע גבייה רק בין ${startLabel} ל-${endLabel}`;
  }, [payAllWindow]);

  const handlePayAllConfirm = async () => {
    const success = await payAllInstallmentsBankTransferOperation(
      filters.selectedMonth,
      setPayAllLoading,
      () => {
        fetchReportData(filters, offset, limit);
      },
    );
    if (success) {
      setShowPayAllWarning(false);
    }
  };

  const handleMasavClick = () => {
    setShowMasavWarning(true);
  };

  const handleMasavConfirm = async () => {
    const success = await generateMasavFileOperation(
      filters.selectedMonth,
      setMasavLoading,
    );
    if (success) {
      setShowMasavWarning(false);
    }
  };

  const handleExportToExcel = async () => {
    if (excelLoading || loading) return;

    if (!total || total <= 0) {
      toast.warn('אין נתונים לייצוא');
      return;
    }

    let allRows: InstallmentBankTransferReportRow[] = [];
    await getReportInstallmentBankTransferOperation(
      filters,
      0,
      total,
      (rows) => {
        allRows = rows;
      },
      () => undefined,
      setExcelLoading,
    );

    if (allRows.length === 0) {
      toast.warn('אין נתונים לייצוא');
      return;
    }

    generateAndDownloadExcelFile(
      createInstallmentBankTransferExcelData(
        allRows,
        formatDate,
        getStatusLabel,
      ),
    );
  };

  const handleOnSearch = async (
    searchFilters: InstallmentBankTransferSideBarFilterType,
  ) => {
    await fetchReportData(searchFilters, 0, limit);
  };

  const onPageChange = (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    fetchReportData(filters, newOffset, newLimit);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח הוראות קבע בנקאיות"
        description="דו״ח זה מציג מידע אודות כלל הוראות הקבע הבנקאיות של האירגון. הדו״ח מאפשר מעקב אחר תשלומים חודשיים לפי סטטוס ותאריך."
        metadata={[
          {
            label: 'מטרת הדו״ח',
            value: 'מעקב אחר הוראות קבע בנקאיות וסטטוס התשלומים החודשיים',
          },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="accounting"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <InstallmentBankTransferSideBarFilter onSearch={handleOnSearch} />
        </div>

        {/* Table Section */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <div style={{ fontSize: '14px', color: '#4b5563' }}>
              סה״כ רשומות: {total.toLocaleString()}
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                style={{
                  height: '30px',
                  width: '30px',
                  backgroundColor: '#10b981',
                  borderColor: '#10b981',
                }}
                icon="pi pi-file-excel"
                tooltip="ייצוא לאקסל"
                loading={excelLoading}
                disabled={excelLoading || loading || total <= 0}
                onClick={handleExportToExcel}
              />

              <Button
                style={{
                  height: '30px',
                  width: '30px',
                }}
                icon="pi pi-download"
                tooltip="הפקה והורדה של קובץ מס״ב"
                loading={masavLoading}
                disabled={!filters.selectedMonth || loading}
                /*disabled={true}*/
                onClick={handleMasavClick}
              />

              <Button
                label="גבה את כל התשלומים"
                icon="pi pi-credit-card"
                className="p-button-success"
                loading={payAllLoading}
                outlined={true}
                style={{
                  height: '30px',
                }}
                disabled={!filters.selectedMonth || loading || !canPayAll}
                /*disabled={true}*/
                onClick={handlePayAllClick}
                tooltip={
                  !filters.selectedMonth
                    ? 'יש לבחור חודש כדי לגבות תשלומים'
                    : !canPayAll
                      ? payAllBlockedTooltip
                      : 'לחץ לגביית כל התשלומים של החודש הנבחר'
                }
                tooltipOptions={{ position: 'top', showOnDisabled: true }}
              />
            </div>
          </div>

          <DataTable
            value={data}
            rowHover
            loading={loading}
            rowClassName={() => 'cursor-pointer'}
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy={true}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="installmentPlanId"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{
              direction: 'rtl',
            }}
            className="installment-bank-transfer-table"
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="firstName"
              header="שם משלם"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <label>
                  {rowData.firstName} {rowData.lastName}
                </label>
              )}
            />
            <Column
              field="bankNumber"
              header="בנק"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="branchNumber"
              header="סניף"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="accountNumber"
              header="חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="totalAmount"
              header="סכום כולל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => `₪${rowData.totalAmount?.toLocaleString()}`}
            />
            <Column
              field="numberOfInstallments"
              header="מספר תשלומים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="installmentNumber"
              header="תשלום מספר"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                `${rowData.installmentNumber} / ${rowData.numberOfInstallments}`
              }
            />
            <Column
              field="amount"
              header="סכום תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => `₪${rowData.amount?.toLocaleString()}`}
            />
            <Column
              field="dueDate"
              header="תאריך לתשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.dueDate)}
            />
            <Column
              field="paidAt"
              header="תאריך תשלום בפועל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                rowData.paidAt ? formatDate(rowData.paidAt) : '-'
              }
            />
            <Column
              field="firstPaymentDate"
              header="תאריך תשלום ראשון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.firstPaymentDate)}
            />
            <Column
              field="paymentDayOfMonth"
              header="יום חיוב בחודש"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="status"
              header="סטטוס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <Tag
                  style={{ minWidth: '100px' }}
                  value={getStatusLabel(rowData.status)}
                  severity={getStatusSeverity(rowData.status)}
                />
              )}
            />
            <Column
              header="פעולות"
              style={{ textAlign: 'right', width: '160px' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                rowData.status === PaymentInstallmentStatus.PAID ? (
                  <Button
                    type="button"
                    label="החזר וכישלון"
                    severity="danger"
                    outlined
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRefundFailRow(rowData);
                    }}
                  />
                ) : null
              }
            />
          </DataTable>
        </div>
      </div>

      <WarningDialog
        header="גביית כל התשלומים"
        isVisible={showPayAllWarning}
        onHide={() => setShowPayAllWarning(false)}
        addIcon={true}
        content={`האם אתה בטוח שברצונך לגבות את כל התשלומים עבור ${getSelectedMonthLabel()}?`}
        dangerousContent="פעולה זו תגבה את כל התשלומים הממתינים בהוראות קבע בנקאיות עבור החודש הנבחר."
        smallLetters="לאחר ביצוע הפעולה, לא ניתן יהיה לבטל את החיובים שבוצעו."
        notInformation="שים לב: הפעולה תגבה את כל התשלומים הממתינים בחודש הנבחר. ודא שבחרת את החודש הנכון לפני האישור."
        onConfirm={handlePayAllConfirm}
        confirmLabel="אישור גבייה"
        confirmLoading={payAllLoading}
        cancelLabel="ביטול"
        disableApproveButton={!canPayAll}
        approveButtonToolTip={payAllBlockedTooltip}
      />

      <RefundAndFailBankTransferInstallmentDialog
        visible={refundFailRow !== null}
        row={refundFailRow}
        onHide={() => setRefundFailRow(null)}
        onSuccess={() => fetchReportData(filters, offset, limit)}
      />

      <WarningDialog
        header="הפקת קובץ מס״ב"
        isVisible={showMasavWarning}
        onHide={() => setShowMasavWarning(false)}
        addIcon={true}
        content={`האם אתה בטוח שברצונך להפיק ולהוריד קובץ מס״ב עבור ${getSelectedMonthLabel()}?`}
        dangerousContent="הקובץ כולל פרטים פרטיים ורגישים של משלמים (כגון פרטי חשבון/בנק)."
        smallLetters="יש לשמור את הקובץ במקום מאובטח ולהעביר אותו רק לגורמים מורשים."
        notInformation="אם אינך בטוח, מומלץ לבטל ולא להוריד את הקובץ."
        onConfirm={handleMasavConfirm}
        confirmLabel="המשך והורד"
        confirmLoading={masavLoading}
        cancelLabel="ביטול"
      />
    </div>
  );
};
